import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";

import { getToken } from "../../../../api/Account";
import { deleteCuotaByIdAsync, getPrestamoByIdAsync } from "../../../../api/Employees";
import { EstadoChip, useAviso } from "../../../../components/base";
import { formatearFecha, formatearMonto } from "../../../../helpers/formato";
import HistorialTab from "../detalleEmployee/HistorialTab";
import AddAbono from "./AddAbono";
import {
  DatoResumen,
  DialogoFormulario,
  celdaFecha,
  celdaMonto,
  useCargaLocal,
  useConfirmacion,
  useEscritura,
} from "../comunes";

// Las cuotas que cobra el sistema solo (la nómina al cerrar y la liquidación) no se anulan a mano:
// el servidor las rechaza. Mismos conceptos que EmployeeHelper (ConceptoCuotaNomina y
// ConceptoCuotaLiquidacion).
const CONCEPTOS_AUTOMATICOS = ["CUOTA DE PRESTAMO", "LIQUIDACION - SALDO DE PRESTAMO"];
const esCuotaAutomatica = (concepto) =>
  CONCEPTOS_AUTOMATICOS.some((prefijo) =>
    String(concepto ?? "").toUpperCase().startsWith(prefijo)
  );
const AVISO_CUOTA_AUTOMATICA = "Cobrada en nómina: no se anula aquí";

export const EstadoPrestamo = ({ cancelado }) =>
  cancelado ? (
    <EstadoChip estado="neutro" label="Cancelado" />
  ) : (
    <EstadoChip estado="info" label="Activo" />
  );

/**
 * Detalle de un préstamo: sus datos y la lista de abonos, con "Abonar" y "Anular".
 * `onCambio` avisa a la lista de préstamos que algo cambió (nuevo abono o abono anulado).
 */
const UpdatePrestamo = ({ selected, setShowModal, onCambio, puedeAbonar, puedeAnular }) => {
  const token = getToken();
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir, dialogo } = useConfirmacion();
  const { cargando, error, cargar } = useCargaLocal(() => getPrestamoByIdAsync(token, selected.id));

  const [prestamo, setPrestamo] = useState(null);
  const [localReload, setLocalReload] = useState(false);
  const [showAbonoModal, setShowAbonoModal] = useState(false);

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setPrestamo(result.data.result);
    })();
    return () => {
      vigente = false;
    };
  }, [localReload]);

  const abonos = prestamo?.abonos ?? [];
  const abonado = abonos.reduce((total, abono) => total + abono.montoAbono, 0);
  const saldo = prestamo ? prestamo.monto - abonado : 0;

  const cambio = () => {
    setLocalReload((actual) => !actual);
    if (onCambio) onCambio();
  };

  const anularAbono = async (abono) => {
    const ok = await escribir(
      () => deleteCuotaByIdAsync(token, abono.id),
      "No se pudo anular el abono"
    );
    if (!ok) return;
    aviso.exito("Abono anulado");
    cambio();
  };

  const pedirAnular = (abono) =>
    pedir({
      titulo: "Anular abono",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se anulará el abono #${abono.id} de ${formatearMonto(
              abono.montoAbono
            )} del préstamo #${selected.id}. Ese monto vuelve al saldo del préstamo.`}
          </Typography>
          <Typography component="p" sx={{ mb: 1, color: "text.secondary" }}>
            Solo se pueden anular los abonos manuales: los que cobró una nómina o una liquidación no.
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Esta acción no se puede deshacer.
          </Typography>
        </>
      ),
      textoConfirmar: "Anular abono",
      peligro: true,
      onConfirmar: () => anularAbono(abono),
    });

  const columnas = [
    {
      id: "id",
      etiqueta: "#",
      desde: "sm",
      sx: { fontVariantNumeric: "tabular-nums" },
      render: (row) => row.id,
    },
    {
      id: "fecha",
      etiqueta: "Fecha de pago",
      sx: celdaFecha,
      render: (row) => formatearFecha(row.fechaPago),
    },
    {
      id: "concepto",
      etiqueta: "Concepto",
      desde: "sm",
      render: (row) => (
        <>
          {row.concepto}
          {esCuotaAutomatica(row.concepto) ? (
            <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
              {AVISO_CUOTA_AUTOMATICA}
            </Typography>
          ) : null}
        </>
      ),
    },
    {
      id: "monto",
      etiqueta: "Monto",
      alinear: "right",
      sx: celdaMonto,
      render: (row) => (
        <Typography variant="monto" component="span">
          {formatearMonto(row.montoAbono)}
        </Typography>
      ),
    },
  ];

  const resumen = prestamo ? (
    <Box
      component="dl"
      sx={{
        m: 0,
        mb: 2,
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
      }}
    >
      <DatoResumen etiqueta="Descripción">
        <Typography>{prestamo.descripcion || "—"}</Typography>
      </DatoResumen>
      <DatoResumen etiqueta="Fecha">
        <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatearFecha(prestamo.fechaCreacion)}
        </Typography>
      </DatoResumen>
      <DatoResumen etiqueta="Estado">
        <EstadoPrestamo cancelado={prestamo.isCanceled} />
      </DatoResumen>
      <DatoResumen etiqueta="Monto">
        <Typography variant="monto">{formatearMonto(prestamo.monto)}</Typography>
      </DatoResumen>
      <DatoResumen etiqueta="Cuota">
        <Typography variant="monto">{formatearMonto(prestamo.cuota)}</Typography>
      </DatoResumen>
      <DatoResumen etiqueta="Abonado">
        <Typography variant="monto">{formatearMonto(abonado)}</Typography>
      </DatoResumen>
      <DatoResumen etiqueta="Saldo">
        <Typography variant="monto">{formatearMonto(saldo)}</Typography>
      </DatoResumen>
    </Box>
  ) : null;

  return (
    <>
      <DialogoFormulario
        titulo={`Préstamo #${selected.id}`}
        ancho="md"
        sinAcciones
        onCerrar={() => setShowModal(false)}
      >
        <HistorialTab
          titulo="Abonos"
          descripcion="Pagos hechos al préstamo, a mano o descontados en una nómina."
          etiquetaTabla={`Abonos del préstamo #${selected.id}`}
          agregar={
            puedeAbonar && prestamo && !prestamo.isCanceled
              ? {
                  etiqueta: "Agregar abono",
                  icono: <AddCardOutlinedIcon />,
                  onClick: () => setShowAbonoModal(true),
                }
              : undefined
          }
          cargando={cargando}
          error={error}
          onReintentar={() => setLocalReload((actual) => !actual)}
          etiquetaCarga="Cargando abonos…"
          filas={abonos}
          columnas={columnas}
          resumen={resumen}
          detalleMovil={(row) =>
            esCuotaAutomatica(row.concepto) ? AVISO_CUOTA_AUTOMATICA : null
          }
          etiquetaFila={(row) => `abono #${row.id}`}
          vacio={{
            titulo: "Sin abonos",
            descripcion: "Aquí aparecen los abonos del préstamo.",
          }}
          acciones={(row) => [
            {
              id: "anular",
              etiqueta: "Anular",
              icono: <DeleteOutlineIcon />,
              peligro: true,
              // Un abono de nómina o de liquidación el servidor siempre lo rechaza: no se ofrece.
              visible: puedeAnular && !esCuotaAutomatica(row.concepto),
              onClick: () => pedirAnular(row),
            },
          ]}
        />
      </DialogoFormulario>

      {showAbonoModal ? (
        <AddAbono
          id={selected.id}
          saldo={saldo}
          setShowModal={setShowAbonoModal}
          reload={localReload}
          setReload={cambio}
        />
      ) : null}

      {dialogo}
    </>
  );
};

export default UpdatePrestamo;
